import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { agencySessionKey, subscribeAgencySession } from "../../../../utils/agencySession.ts";
import AppFeedback from "../../AppFeedback.tsx";
import { t } from "../../../../features/store-language/i18n.ts";
import { gridPreferenceRegistry } from "./gridPreferenceRegistry";
import { columnIds, sanitizeGridState } from "./gridPreferenceState";
import { preferenceRequest } from "./gridPreferenceApi";
import { GridPreferenceSaver } from "./GridPreferenceSaver";

const entries = new Map();
subscribeAgencySession(function () {
  for (const [key, entry] of entries) {
    if (entry.kind === "agency") {
      entry.controller.abort();
      entry.saver?.dispose();
      entries.delete(key);
    }
  }
});

function report(error) {
  if (error?.name !== "AbortError" && error?.code !== "ERR_CANCELED") AppFeedback.httpError(error);
}

function createEntry(key, registration, identity, session, ids) {
  const entry = {
    kind: registration.kind, controller: new AbortController(), listeners: new Set(),
    ready: false, canSave: false, state: undefined, revision: 0, resetting: false,
  };
  function request(method, state, signal) {
    return preferenceRequest(registration, method, state, identity, session, signal);
  }
  entry.request = request;
  entry.emit = function () { entry.listeners.forEach(function (listener) { listener(); }); };
  entry.loaded = request("get", undefined, entry.controller.signal).then(function (result) {
    if (entry.controller.signal.aborted) return;
    if (result && (result.gridKey !== registration.key || result.layoutVariant !== "desktop"
        || result.schemaVersion !== 1 || !result.state || typeof result.state !== "object" || Array.isArray(result.state))) {
      throw new Error(t("پاسخ تنظیمات جدول نامعتبر است."));
    }
    entry.state = sanitizeGridState(result?.state, ids);
    entry.canSave = true;
    entry.saver = new GridPreferenceSaver(
      function (state, signal) { return request("put", state, signal); },
      report,
      function (state) { entry.state = entry.saver.pending || state; },
      entry.state,
    );
  }).catch(function (error) {
    if (!entry.controller.signal.aborted) report(error);
  }).finally(function () {
    entry.ready = true;
    entry.emit();
    if (!entry.canSave && entry.listeners.size === 0 && entries.get(key) === entry) entries.delete(key);
  });
  entries.set(key, entry);
  return entry;
}

export default function useGridPreference(id, columns, mobile, identity) {
  const userChange = useRef(false);
  const session = useSyncExternalStore(subscribeAgencySession, agencySessionKey);
  const registration = gridPreferenceRegistry[id];
  const owner = registration?.kind === "platform" ? identity?.userId : session;
  const idsKey = columnIds(columns).sort().join("|");
  const key = registration && owner ? registration.kind + ":" + owner + ":" + registration.key : null;
  const [, update] = useState(0);
  const entry = useMemo(function () {
    if (!key || !idsKey) return null;
    const existing = entries.get(key);
    // A schema mismatch must never let one grid overwrite another layout.
    if (existing && existing.idsKey !== idsKey) {
      return { ready: true, canSave: false, state: undefined, revision: 0, listeners: new Set() };
    }
    const resolved = existing || createEntry(key, registration, identity, session, idsKey.split("|"));
    resolved.idsKey = idsKey;
    return resolved;
    // Identity CSRF tokens may refresh without changing the preference owner.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, idsKey]);

  useEffect(function () {
    if (!entry) return;
    const listener = function () { update(function (value) { return value + 1; }); };
    entry.listeners.add(listener);
    if (entry.ready) listener();
    return function () {
      entry.listeners.delete(listener);
      // Commit a pending user edit on route exit, but never retry failed payloads.
      void entry.saver?.flush();
      if (entry.ready && !entry.canSave && entries.get(key) === entry) entries.delete(key);
    };
  }, [entry, key]);

  const initialState = useMemo(function () {
    const state = sanitizeGridState(entry?.state, idsKey.split("|"));
    if (state && mobile) {
      delete state.columnPinning;
      delete state.sideBar;
    }
    return state;
    // Snapshot only on hydration/reset/remount, never reapply following an autosave.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry, entry?.ready, entry?.revision, mobile, idsKey]);

  async function reset() {
    if (!entry?.canSave || entry.resetting) return;
    entry.resetting = true;
    entry.emit();
    await entry.saver.pauseAndWait();
    try {
      await entry.request("delete", undefined, entry.controller.signal);
      entry.state = undefined;
      entry.saver.lastSaved = undefined;
      entry.saver.lastAttempted = undefined;
      entry.revision++;
      try {
        localStorage.removeItem(id);
        localStorage.removeItem("filtering/" + id);
      } catch { /* Legacy browser storage is optional. */ }
    } catch (error) {
      report(error);
    } finally {
      entry.saver.resume();
      entry.resetting = false;
      entry.emit();
    }
  }

  return {
    enabled: Boolean(registration),
    hydrating: Boolean(registration && (!entry || !entry.ready)),
    key: key + ":" + (entry?.revision || 0) + ":" + mobile,
    initialState,
    resetting: entry?.resetting,
    canReset: Boolean(entry?.canSave && !mobile && !entry.resetting),
    reset,
    capture: function (event) {
      if (["toolPanelDragAndDrop", "toolPanelUi", "uiColumnMoved", "uiColumnResized",
        "uiColumnDragged", "uiColumnSorted", "contextMenu", "columnMenu", "autosizeColumns",
        "columnFilter", "sideBarButtonClicked"].includes(event.source)) userChange.current = true;
    },
    schedule: function (event) {
      const changedByUser = userChange.current;
      userChange.current = false;
      if (!entry?.ready || !entry.canSave || mobile || entry.resetting
          || !changedByUser
          || event.sources?.some(function (source) { return ["gridInitializing", "api"].includes(source); })) return;
      const state = sanitizeGridState(event.state, idsKey.split("|"));
      if (state) {
        entry.state = state;
        entry.saver.schedule(state);
      }
    },
  };
}

export function clearPlatformGridPreferences() {
  for (const [key, entry] of entries) {
    if (entry.kind === "platform") {
      entry.controller.abort();
      entry.saver?.dispose();
      entries.delete(key);
    }
  }
}
